import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage, languages } from '@/i18n/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Loader2, Mail, Lock, User, Eye, EyeOff, Wallet, Globe, Key } from 'lucide-react';
import '@/App.css';

const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [invitationCode, setInvitationCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [requiresInvitation, setRequiresInvitation] = useState(false);
  const { register } = useAuth();
  const { t, language, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Check if invitation code is required
    const checkSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/registration-settings`);
        if (response.ok) {
          const data = await response.json();
          setRequiresInvitation(data.require_invitation);
        }
      } catch (e) {
        console.log('Could not check registration settings');
      }
    };
    checkSettings();
  }, [API_URL]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t('passwordsDontMatch'));
      return;
    }

    if (password.length < 6) {
      setError(t('passwordTooShort'));
      return;
    }

    setLoading(true);

    try {
      await register(email, password, name, invitationCode || null, language);
      navigate('/verify-email', { state: { email } });
    } catch (err) {
      setError(err.response?.data?.detail || t('emailAlreadyRegistered'));
    } finally {
      setLoading(false);
    }
  };

  const currentLang = languages.find(l => l.code === language);

  return (
    <div className="min-h-screen flex bg-zinc-950">
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4 z-50">
        <div className="relative">
          <button
            onClick={() => setShowLangMenu(!showLangMenu)}
            className="flex items-center gap-2 px-3 py-2 bg-zinc-900/80 border border-zinc-800 rounded-lg text-zinc-300 hover:bg-zinc-800 transition-colors"
            data-testid="language-selector"
          >
            <Globe className="h-4 w-4" />
            <span>{currentLang?.flag} {currentLang?.name}</span>
          </button>
          
          {showLangMenu && (
            <div className="absolute right-0 mt-2 w-40 bg-zinc-900 border border-zinc-800 rounded-lg shadow-xl overflow-hidden">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => { changeLanguage(lang.code); setShowLangMenu(false); }}
                  className={`w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-zinc-800 transition-colors ${
                    language === lang.code ? 'bg-purple-600/20 text-purple-400' : 'text-zinc-300'
                  }`}
                  data-testid={`lang-${lang.code}`}
                >
                  <span>{lang.flag}</span>
                  <span>{lang.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Left side - Image */}
      <div className="hidden lg:flex lg:flex-1 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-900/40 via-purple-900/40 to-fuchsia-900/40"></div>
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1642790106117-e829e14a795f?auto=format&fit=crop&w=1200&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-l from-zinc-950 via-transparent to-transparent"></div>
        
        <div className="absolute bottom-12 left-12 right-12 z-10">
          <div className="bg-zinc-900/60 backdrop-blur-xl rounded-2xl p-8 border border-zinc-800/50">
            <h2 className="text-4xl font-black text-white mb-4">
              {t('heroRegisterTitle')} <span className="bg-gradient-to-r from-purple-400 to-fuchsia-400 bg-clip-text text-transparent">WSFinances</span>
            </h2>
            <p className="text-zinc-300 text-lg">
              {t('heroRegisterDesc')}
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 relative">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -left-40 w-80 h-80 bg-violet-600/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-purple-600/20 rounded-full blur-3xl"></div>
        </div>
        
        <Card className="w-full max-w-md bg-zinc-900/80 border-zinc-800 backdrop-blur-xl relative z-10 shadow-2xl shadow-purple-900/20">
          <CardHeader className="space-y-1 text-center pb-6">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/30">
                <Wallet className="h-6 w-6 text-white" />
              </div>
            </div>
            <CardTitle className="text-3xl font-black tracking-tight">
              <span className="bg-gradient-to-r from-violet-400 via-purple-400 to-fuchsia-400 bg-clip-text text-transparent">
                {t('createAccount')}
              </span>
            </CardTitle>
            <CardDescription className="text-zinc-400">
              {t('registerTitle')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm animate-fade-in">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="name" className="text-zinc-300 text-sm font-medium">{t('name')}</Label>
                <div className="relative group">
                  <User className="absolute left-3 top-3 h-5 w-5 text-zinc-500 group-focus-within:text-purple-400 transition-colors" />
                  <Input
                    id="name"
                    type="text"
                    placeholder={t('name')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-11 h-12 bg-zinc-950/50 border-zinc-800 focus:border-purple-500 focus:ring-purple-500/20 transition-all"
                    data-testid="register-name-input"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-zinc-300 text-sm font-medium">{t('email')}</Label>
                <div className="relative group">
                  <Mail className="absolute left-3 top-3 h-5 w-5 text-zinc-500 group-focus-within:text-purple-400 transition-colors" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-11 h-12 bg-zinc-950/50 border-zinc-800 focus:border-purple-500 focus:ring-purple-500/20 transition-all"
                    data-testid="register-email-input"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-zinc-300 text-sm font-medium">{t('password')}</Label>
                <div className="relative group">
                  <Lock className="absolute left-3 top-3 h-5 w-5 text-zinc-500 group-focus-within:text-purple-400 transition-colors" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-11 pr-11 h-12 bg-zinc-950/50 border-zinc-800 focus:border-purple-500 focus:ring-purple-500/20 transition-all"
                    data-testid="register-password-input"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-zinc-500 hover:text-purple-400 transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-zinc-300 text-sm font-medium">{t('confirmPassword')}</Label>
                <div className="relative group">
                  <Lock className="absolute left-3 top-3 h-5 w-5 text-zinc-500 group-focus-within:text-purple-400 transition-colors" />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-11 pr-11 h-12 bg-zinc-950/50 border-zinc-800 focus:border-purple-500 focus:ring-purple-500/20 transition-all"
                    data-testid="register-confirm-password-input"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-3 text-zinc-500 hover:text-purple-400 transition-colors"
                    data-testid="toggle-confirm-password-visibility"
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Invitation Code Field */}
              {requiresInvitation && (
                <div className="space-y-2">
                  <Label htmlFor="invitationCode" className="text-zinc-300 text-sm font-medium">
                    Código de Invitación <span className="text-red-400">*</span>
                  </Label>
                  <div className="relative group">
                    <Key className="absolute left-3 top-3 h-5 w-5 text-zinc-500 group-focus-within:text-purple-400 transition-colors" />
                    <Input
                      id="invitationCode"
                      type="text"
                      placeholder="XXXXXXXX"
                      value={invitationCode}
                      onChange={(e) => setInvitationCode(e.target.value.toUpperCase())}
                      className="pl-11 h-12 bg-zinc-950/50 border-zinc-800 focus:border-purple-500 focus:ring-purple-500/20 transition-all font-mono"
                      data-testid="register-invitation-code-input"
                      required={requiresInvitation}
                      maxLength={12}
                    />
                  </div>
                  <p className="text-xs text-zinc-500">Necesitas un código de invitación para registrarte</p>
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full h-12 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white font-semibold shadow-lg shadow-violet-500/25 transition-all hover:shadow-violet-500/40 mt-2"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {t('registering')}
                  </>
                ) : (
                  t('createAccount')
                )}
              </Button>

              <p className="text-center text-sm text-zinc-400 pt-2">
                {t('hasAccount')}{' '}
                <Link 
                  to="/login" 
                  className="text-purple-400 hover:text-purple-300 font-medium transition-colors"
                  data-testid="login-link"
                >
                  {t('login')}
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
